import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import DictionaryService from '../../../services/dictionary.service';

@Component({
	standalone: true,
	selector: 'toolkit-eek2-therapist-results',
	templateUrl: './eek2-therapist-results.component.html',
	styleUrl: './eek2-therapist-results.component.scss',
	imports: [CommonModule]
})
export class ToolkitEek2TherapistResultsComponent implements OnChanges {
	private dictionaryService: DictionaryService = inject(DictionaryService);

	@Input() assessment: any = null;
	@Input() questions: any = null;
	@Input() assignment: any = null;
	@Input() historyAssignments: any[] = [];

	vm: any = {
		current: null,
		history: [],
		mainRows: [],
		additionalRows: [],
		itemRows: [],
		trendCards: [],
		comorbid: false,
		suicideRisk: false,
		suicideValue: null
	};

	private readonly therapistSubscales = [
		{ code: 'DEP', key: 'depression', cutoff: 12, itemOrders: [1, 2, 3, 4, 5, 6, 7, 8] },
		{ code: 'YAR', key: 'anxiety', cutoff: 12, itemOrders: [9, 10, 11, 12, 13, 14] },
		{ code: 'PAF', key: 'panic', cutoff: 7, itemOrders: [15, 16, 17, 18, 19] },
		{ code: 'VAS', key: 'asthenia', cutoff: 8, itemOrders: [22, 23, 24, 25] },
		{ code: 'INS', key: 'insomnia', cutoff: 6, itemOrders: [26, 27, 28] }
	];

	ngOnChanges(_changes: SimpleChanges) {
		this.rebuild();
	}

	get isEt(): boolean {
		return (this.dictionaryService.getCurrentLocale() || 'en_GB').toString().toLowerCase().startsWith('et');
	}

	get copy() {
		return {
			disclaimerTitle: this.tr('Toolkit.EEK2.Therapist.Disclaimer.Title'),
			disclaimerBody: this.tr('Toolkit.EEK2.Therapist.Disclaimer.Body'),
			mainSubscales: this.tr('Toolkit.EEK2.Therapist.MainSubscales'),
			additionalSubscales: this.tr('Toolkit.EEK2.Therapist.AdditionalSubscales'),
			colSubscale: this.tr('Toolkit.EEK2.Therapist.Col.Subscale'),
			colScore: this.tr('Toolkit.EEK2.Therapist.Col.Score'),
			colCutoff: this.tr('Toolkit.EEK2.Therapist.Col.Cutoff'),
			colStatus: this.tr('Toolkit.EEK2.Therapist.Col.Status'),
			aboveShort: this.tr('Toolkit.EEK2.Therapist.Status.Above'),
			belowShort: this.tr('Toolkit.EEK2.Therapist.Status.Below'),
			itemResponsesTitle: this.tr('Toolkit.EEK2.Therapist.ItemResponsesTitle'),
			colItem: this.tr('Toolkit.EEK2.Therapist.Col.Item'),
			colResponse: this.tr('Toolkit.EEK2.Therapist.Col.Response'),
			notScoredTag: this.tr('Toolkit.EEK2.Therapist.NotScoredTag'),
			scoredTag: (code: string) => this.tr('Toolkit.EEK2.Therapist.ScoredTag').replace('{code}', code),
			historyTitle: this.tr('Toolkit.EEK2.Therapist.HistoryTitle'),
			date: this.tr('Toolkit.EEK2.Therapist.Col.Date'),
			comorbidTitle: this.tr('Toolkit.EEK2.Therapist.Comorbid.Title'),
			comorbidBody: this.tr('Toolkit.EEK2.Therapist.Comorbid.Body'),
			suicideTitle: this.tr('Toolkit.EEK2.Therapist.Suicide.Title'),
			suicideBody: (v: number) => this.tr('Toolkit.EEK2.Therapist.Suicide.Body').replace('{v}', `${v}`),
			trendCutoff: (c: number) => this.tr('Toolkit.EEK2.Therapist.Trend.Cutoff').replace('{c}', `${c}`),
			trendDelta: (d: string) => this.tr('Toolkit.EEK2.Therapist.Trend.Delta').replace('{delta}', d),
			flatTrend: this.tr('Toolkit.EEK2.Therapist.Trend.Flat'),
			towardCutoff: this.tr('Toolkit.EEK2.Therapist.Trend.Toward'),
			awayCutoff: this.tr('Toolkit.EEK2.Therapist.Trend.Away')
		};
	}

	private tr(key: string, fallback?: string): string {
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : (fallback || key);
	}

	private rebuild() {
		const items = this.sortedItems();
		const history = (Array.isArray(this.historyAssignments) ? this.historyAssignments : [])
			.filter((assignment) => this.matchesAssessment(assignment) && this.extractAnswers(assignment, items.length).length >= items.length)
			.map((assignment) => this.toRecord(assignment, items))
			.filter((record) => !!record)
			.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

		const current = this.assignment ? this.toRecord(this.assignment, items) : history[0] || null;
		if (current && !history.some((record) => record.id === current.id)) history.unshift(current);

		this.vm.history = history;
		this.vm.current = current;

		if (!current) {
			this.vm.mainRows = [];
			this.vm.additionalRows = [];
			this.vm.itemRows = [];
			this.vm.trendCards = [];
			this.vm.comorbid = false;
			this.vm.suicideRisk = false;
			this.vm.suicideValue = null;
			return;
		}

		this.vm.mainRows = this.therapistSubscales.slice(0, 3).map((sub) => this.subscaleRow(current, sub));
		this.vm.additionalRows = this.therapistSubscales.slice(3).map((sub) => this.subscaleRow(current, sub));
		this.vm.itemRows = items.map((item) => this.itemRow(current, item));
		this.vm.trendCards = this.therapistSubscales.map((sub) => this.trendCard(history, sub));
		this.vm.comorbid = !!(current.scores.DEP?.above && current.scores.YAR?.above);
		this.vm.suicideValue = current.responses[5];
		this.vm.suicideRisk = Number(current.responses[5]) >= 2;
	}

	private sortedItems(): any[] {
		const instrument = this.questions?.instrument || this.questions || {};
		const items = Array.isArray(instrument?.items) ? instrument.items : [];
		return items.slice().sort((a, b) => (a?.order || 0) - (b?.order || 0));
	}

	private matchesAssessment(assignment: any): boolean {
		const assignmentAssessmentId = (assignment?.assessmentId ?? assignment?._assessment?.id ?? '').toString();
		const currentAssessmentId = (this.assignment?.assessmentId ?? this.assessment?.id ?? '').toString();
		return !!assignmentAssessmentId && !!currentAssessmentId && assignmentAssessmentId === currentAssessmentId;
	}

	private extractAnswers(assignment: any, itemCount: number): any[] {
		const raw = assignment?.answersJson || assignment?.answers_json || null;
		if (!raw) return [];
		try {
			const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
			return Array.isArray(parsed) ? parsed.slice(0, itemCount) : [];
		} catch {
			return [];
		}
	}

	private toRecord(assignment: any, items: any[]): any | null {
		const answers = this.extractAnswers(assignment, items.length);
		if (answers.length < items.length) return null;
		const responses: any = {};
		items.forEach((item, index) => {
			responses[item.order] = answers[index];
		});
		const timestamp = assignment?.completedAt || assignment?.updatedAt || assignment?.created || assignment?.assignedAt || new Date().toISOString();
		return {
			id: (assignment?.id ?? timestamp).toString(),
			timestamp,
			answers,
			responses,
			scores: this.computeScores(responses)
		};
	}

	private computeScores(responses: any): any {
		const out: any = {};
		this.therapistSubscales.forEach((sub) => {
			const score = sub.itemOrders.reduce((sum, order) => {
				const value = Number(responses[order]);
				return sum + (isFinite(value) ? value : 0);
			}, 0);
			out[sub.code] = {
				score,
				max: sub.itemOrders.length * 4,
				cutoff: sub.cutoff,
				above: score >= sub.cutoff
			};
		});
		return out;
	}

	private subscaleRow(record: any, sub: any): any {
		const score = record.scores[sub.code];
		return {
			code: sub.code,
			label: this.subscaleLabel(sub.key),
			score: score.score,
			max: score.max,
			cutoff: score.cutoff,
			above: score.above,
			status: score.above ? this.copy.aboveShort : this.copy.belowShort
		};
	}

	private itemRow(record: any, item: any): any {
		const value = record.responses[item.order];
		const code = this.therapistSubscales.find((sub) => sub.itemOrders.includes(item.order))?.code || null;
		return {
			order: item.order,
			text: this.tr(`Toolkit.EEK2.Item.${item.order}`, item?.text || item?.textEt || ''),
			value,
			scaleLabel: value != null ? this.responseScaleLabel(value) : '',
			tag: code ? this.copy.scoredTag(code) : this.copy.notScoredTag,
			notScored: !code,
			risk: item.order === 5
		};
	}

	private trendCard(history: any[], sub: any): any {
		const chrono = history.slice().reverse();
		const values = chrono.map((record) => record.scores[sub.code]?.score || 0);
		const first = values[0] ?? 0;
		const last = values[values.length - 1] ?? 0;
		const delta = last - first;
		const deltaStr = `${delta > 0 ? '+' : ''}${delta}`;
		const trendWord = values.length < 2 || delta === 0 ? this.copy.flatTrend : delta > 0 ? this.copy.towardCutoff : this.copy.awayCutoff;
		return {
			label: this.subscaleLabel(sub.key),
			code: sub.code,
			meta: `${this.copy.trendCutoff(sub.cutoff)} · ${this.copy.trendDelta(deltaStr)} · ${trendWord}`,
			sparkline: this.sparkline(values, sub.itemOrders.length * 4, sub.cutoff)
		};
	}

	private sparkline(values: number[], max: number, cutoff: number) {
		const w = 200;
		const h = 56;
		const pad = 4;
		const innerW = w - pad * 2;
		const innerH = h - pad * 2;
		const x = (i: number) => values.length <= 1 ? pad + innerW / 2 : pad + (i / (values.length - 1)) * innerW;
		const y = (value: number) => pad + innerH - Math.max(0, Math.min(1, value / max)) * innerH;
		const points = values.map((value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ');
		const cutoffY = y(cutoff).toFixed(1);
		const lastOver = (values[values.length - 1] || 0) >= cutoff;
		return {
			cutoffY,
			pathPoints: points,
			lineColor: lastOver ? 'var(--eek2-pink)' : 'var(--eek2-green)',
			dots: values.map((value, index) => ({
				x: x(index).toFixed(1),
				y: y(value).toFixed(1),
				color: value >= cutoff ? 'var(--eek2-pink)' : 'var(--eek2-green)'
			}))
		};
	}

	private responseScaleLabel(value: number): string {
		const scale = this.questions?.responseScale || this.questions?.instrument?.responseScale || [];
		const option = Array.isArray(scale) ? scale.find((entry: any) => Number(entry?.value) === Number(value)) : null;
		return this.tr(`Toolkit.EEK2.Scale.${value}`, option?.label || option?.labelEt || '');
	}

	private subscaleLabel(key: string): string {
		return this.tr(`Toolkit.EEK2.Therapist.Subscale.${key}.Label`);
	}

	formatDate(value: any): string {
		const date = new Date(value);
		if (!isFinite(date.getTime())) return '';
		return new Intl.DateTimeFormat(this.isEt ? 'et-EE' : 'en-GB', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		}).format(date);
	}
}
